import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../../css/HomePage.css";
import backendAPI from "../../../utils/backendAPI";
import Loader from "../../../components/Loader";
import { ToastContext } from "../../../App";
import { logout } from "../../../actions/userActions";
import ProfileDropdown from "../../../components/ProfileDropdown";
import MerchantSidebarMenu from "../../../components/MerchantSidebarMenu";

function formatPhoneDisplay(raw) {
  if (raw == null || raw === "") return "—";
  const s = String(raw).replace(/\D/g, "");
  if (s.length === 10) return `(${s.slice(0, 3)}) ${s.slice(3, 6)}-****`;
  if (s.length === 11 && s[0] === "1") {
    return `(${s.slice(1, 4)}) ${s.slice(4, 7)}-****`;
  }
  return String(raw).trim() || "—";
}

function toDateMaybe(v) {
  if (!v) return null;
  if (v instanceof Date) return v;
  // Firestore Timestamp-like objects
  if (typeof v === "object") {
    if (typeof v.toDate === "function") {
      try {
        const d = v.toDate();
        return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null;
      } catch {
        // ignore
      }
    }
    const sec = v.seconds ?? v._seconds;
    const nano = v.nanoseconds ?? v._nanoseconds ?? 0;
    if (typeof sec === "number" && Number.isFinite(sec)) {
      const ms = sec * 1000 + Math.floor(Number(nano) / 1e6);
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
  }
  // Numeric unix seconds vs millis
  if (typeof v === "number" && Number.isFinite(v)) {
    const ms = v >= 1e9 && v < 1e11 ? v * 1000 : v;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Strings
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatDateTime(value) {
  const d = toDateMaybe(value);
  if (!d) return "—";
  try {
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
}

function primaryRewardRow(customer) {
  const rewards = customer.rewards || [];
  return rewards[0] || null;
}

function totalRewardsRedeemed(customer) {
  const r = primaryRewardRow(customer);
  const n = r
    ? Number(
        r.rewards_claimed ??
          r.rewardsClaimed ??
          r.rewards_claimed_count ??
          r.rewardsClaimedCount ??
          r.claimed ??
          0
      )
    : 0;
  return Number.isFinite(n) ? n : 0;
}

function lastVisitIso(customer) {
  // Prefer last "visit" credit from tap events (authoritative for last-visit display).
  const taps = customer.tap_events || [];
  const visitTaps = taps.filter((ev) => {
    const ct = String(ev?.credit_type || ev?.creditType || "").toLowerCase();
    const et = String(ev?.event_type || ev?.eventType || ev?.type || "").toLowerCase();
    const merged = `${ct} ${et}`.trim();
    return merged.includes("visit");
  });
  if (visitTaps.length > 0) {
    const sorted = [...visitTaps].sort((a, b) => {
      const ta = a.timestamp_server || a.timestampServer || a.timestamp || a.created_at || a.createdAt;
      const tb = b.timestamp_server || b.timestampServer || b.timestamp || b.created_at || b.createdAt;
      const da = (toDateMaybe(ta)?.getTime() || 0);
      const db = (toDateMaybe(tb)?.getTime() || 0);
      return db - da;
    });
    const first = sorted[0];
    return first.timestamp_server || first.timestampServer || first.timestamp || first.created_at || first.createdAt || null;
  }

  // If we can't classify visit taps (missing credit_type/event_type), fall back to latest tap timestamp.
  if (taps.length > 0) {
    const sorted = [...taps].sort((a, b) => {
      const ta = a.timestamp_server || a.timestampServer || a.timestamp || a.created_at || a.createdAt;
      const tb = b.timestamp_server || b.timestampServer || b.timestamp || b.created_at || b.createdAt;
      const da = (toDateMaybe(ta)?.getTime() || 0);
      const db = (toDateMaybe(tb)?.getTime() || 0);
      return db - da;
    });
    const first = sorted[0];
    return first.timestamp_server || first.timestampServer || first.timestamp || first.created_at || first.createdAt || null;
  }

  // Fallback to Visits doc timestamps if no visit tap is present.
  const visits = customer.visits || [];
  const v0 = visits[0] || {};
  return v0.last_visit_at || v0.lastVisitAt || v0.updated_at || v0.updatedAt || null;
}

function lastRedemptionIso(customer) {
  const r = primaryRewardRow(customer);
  if (!r) return null;
  return (
    r.last_reward_claimed_at ||
    r.lastRewardClaimedAt ||
    r.last_reward_claimed ||
    r.lastRewardClaimed ||
    r.updated_at ||
    r.updatedAt ||
    null
  );
}

function numOrNull(v) {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function hasAnySelectedReward(customer) {
  const list = customer.selected_rewards;
  if (Array.isArray(list) && list.length > 0) return true;
  const v = numOrNull(customer.visits_until_next_selected_reward);
  const p = numOrNull(customer.points_until_next_selected_reward);
  return v != null || p != null;
}

/** How many visits until the next visit-based program reward (from API). */
function visitsToNextLabel(customer) {
  const direct = numOrNull(customer.visits_until_next_program_reward);
  const fallback = numOrNull(customer.visits_until_next_selected_reward);
  const n = direct != null ? direct : fallback;
  if (n == null) return "—";
  if (n === 0) return "0 (eligible now)";
  return `${n} visit${n === 1 ? "" : "s"} to claim reward`;
}

/** Smallest “distance” to any configured reward (for sorting). */
function closestRewardDistance(customer) {
  const v = numOrNull(customer.visits_until_next_program_reward);
  if (v == null) return 999;
  return v;
}

function visitsUntilOneToGo(customer) {
  return numOrNull(customer.visits_until_next_program_reward);
}

function pointsBalanceDisplay(customer) {
  const n = Number(customer.points_balance);
  if (Number.isFinite(n)) return n;
  const p = customer.firestore_user || {};
  const fb = Number(p.points_balance);
  return Number.isFinite(fb) ? fb : 0;
}

/** Points from FirestoreUserProfile only (Django `firestore_user.points_balance`), not merchant loyalty totals. */
function firestoreProfilePointsBalance(customer) {
  const p = customer.firestore_user || {};
  const n = Number(p.points_balance);
  return Number.isFinite(n) ? n : 0;
}

function loyaltyIdFromCustomer(customer) {
  const p = customer.firestore_user || {};
  if (p.loyalty_id) return String(p.loyalty_id);
  if (p.firebase_uid) return String(p.firebase_uid);
  return "—";
}

function isRewardLikeTap(ev) {
  const t = (ev?.event_type || "").toLowerCase();
  return t.includes("reward") || t.includes("claim") || t.includes("redeem");
}

function CustomerDetailModal({ show, onHide, customer, businessName }) {
  const { t } = useTranslation()  // ← this was missing
  if (!customer) return null

  const profile = customer.firestore_user || {}
  const visits = customer.visits || []
  const visitRow = visits[0]
  const visitCountRaw =
      visitRow != null
          ? visitRow.visitCount ?? visitRow.visit_count ?? visitRow.visits
          : customer.visits_at_merchant
  const visitCount = Number.isFinite(Number(visitCountRaw)) ? Number(visitCountRaw) : 0
  const rewardRow = primaryRewardRow(customer)
  const tapEvents = [...(customer.tap_events || [])].sort((a, b) => {
    const ta = a.timestamp_server || a.timestampServer || a.timestamp || a.created_at || a.createdAt
    const tb = b.timestamp_server || b.timestampServer || b.timestamp || b.created_at || b.createdAt
    return (toDateMaybe(tb)?.getTime() || 0) - (toDateMaybe(ta)?.getTime() || 0)
  })

  return (
      <Modal show={show} onHide={onHide} size="lg" centered scrollable
             dialogClassName="customers-detail-modal" contentClassName="border-0">
        <Modal.Header closeButton className="border-0 pb-0">
          <Modal.Title as="h5">{t('customers.modal.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-2">
          {businessName ? <p className="klerk-customers-lead mb-3">{businessName}</p> : null}

          <dl className="row mb-0 small">
            <dt className="col-sm-4 text-secondary">{t('customers.modal.phone')}</dt>
            <dd className="col-sm-8">{formatPhoneDisplay(profile.phone)}</dd>
            <dt className="col-sm-4 text-secondary">{t('customers.modal.loyaltyId')}</dt>
            <dd className="col-sm-8 text-break">{loyaltyIdFromCustomer(customer)}</dd>
            <dt className="col-sm-4 text-secondary">{t('customers.modal.visitCount')}</dt>
            <dd className="col-sm-8">{visitCount}</dd>
            <dt className="col-sm-4 text-secondary">{t('customers.modal.visitsToNext')}</dt>
            <dd className="col-sm-8">{visitsToNextLabel(customer)}</dd>
            <dt className="col-sm-4 text-secondary">{t('customers.modal.totalRedeemed')}</dt>
            <dd className="col-sm-8">{totalRewardsRedeemed(customer)}</dd>
            <dt className="col-sm-4 text-secondary">{t('customers.modal.lastVisit')}</dt>
            <dd className="col-sm-8">{formatDateTime(lastVisitIso(customer))}</dd>
            <dt className="col-sm-4 text-secondary">{t('customers.modal.lastRedemption')}</dt>
            <dd className="col-sm-8">{formatDateTime(lastRedemptionIso(customer))}</dd>
          </dl>

          <h6 className="card-title-sm mt-4 mb-2" style={{ fontSize: "1rem" }}>
            {t('customers.modal.redemptionHistory')}
          </h6>
          <p className="klerk-customers-lead mb-3" style={{ fontSize: "0.8125rem" }}>
            {t('customers.modal.redemptionDesc')}
          </p>
          {rewardRow ? (
              <ul className="small mb-3 program-desc">
                <li><strong>{t('customers.modal.lifetimeRedemptions')}</strong> {totalRewardsRedeemed(customer)}</li>
                <li><strong>{t('customers.modal.lastRewardClaimed')}</strong> {formatDateTime(lastRedemptionIso(customer))}</li>
              </ul>
          ) : (
              <p className="klerk-customers-lead mb-3" style={{ fontSize: "0.8125rem" }}>
                {t('customers.modal.noRedemptionHistory')}
              </p>
          )}

          <h6 className="card-title-sm mt-3 mb-2" style={{ fontSize: "1rem" }}>
            {t('customers.modal.recentActivity')}
          </h6>
          {tapEvents.length === 0 ? (
              <p className="klerk-customers-lead mb-0" style={{ fontSize: "0.8125rem" }}>
                {t('customers.modal.noActivity')}
              </p>
          ) : (
              <div className="table-responsive">
                <table className="klerk-customers-modal-table">
                  <thead>
                  <tr>
                    <th>{t('customers.modal.when')}</th>
                    <th>{t('customers.modal.type')}</th>
                    <th>{t('customers.modal.notes')}</th>
                    <th>{t('customers.modal.recorded')}</th>
                  </tr>
                  </thead>
                  <tbody>
                  {tapEvents.map((ev) => (
                      <tr key={ev.id}>
                        <td>{formatDateTime(ev.timestamp_server || ev.timestampServer || ev.timestamp || ev.created_at || ev.createdAt)}</td>
                        <td>{ev.event_type || "—"}</td>
                        <td className="text-break text-muted small">
                          {isRewardLikeTap(ev) ? t('customers.modal.redemptionRelated') : t('customers.modal.visitOther')}
                          {ev.source ? ` · ${ev.source}` : ""}
                          {ev.tag_id ? ` · ${ev.tag_id}` : ""}
                        </td>
                        <td>{ev.credited ? "Yes" : "No"}</td>
                      </tr>
                  ))}
                  </tbody>
                </table>
              </div>
          )}
        </Modal.Body>
        <Modal.Footer className="border-0 pt-0">
          <button type="button" className="klerk-btn btn-secondary" onClick={onHide}>
            {t('customers.modal.close')}
          </button>
        </Modal.Footer>
      </Modal>
  )
}

export default function MerchantCustomersPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const showToast = useContext(ToastContext);
  const { t } = useTranslation();
  const { userInfo } = useSelector((state) => state.userLogin);
  const isAdmin = Boolean(userInfo?.is_admin);

  const logoutHandler = () => {
    showToast?.("You have been logged out successfully.", "info");
    dispatch(logout());
    navigate("/login", { replace: true });
  };

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payload, setPayload] = useState(null);
  const [merchants, setMerchants] = useState([]);
  const [selectedMerchantId, setSelectedMerchantId] = useState("");
  const [sortBy, setSortBy] = useState("recent");
  const [detailCustomer, setDetailCustomer] = useState(null);
  const [onboardingName, setOnboardingName] = useState("");

  useEffect(() => {
    if (!isAdmin) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await backendAPI.get("/api/square/merchants/all/");
        if (cancelled) return;
        const list = res.data?.merchants || [];
        setMerchants(list);
        if (list.length === 1) setSelectedMerchantId(list[0].merchant_id || "");
      } catch (e) {
        if (!cancelled) setMerchants([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const fetchOnboardingBusinessName = async () => {
    try {
      const params =
        isAdmin && selectedMerchantId ? { merchant_id: selectedMerchantId } : {};
      const res = await backendAPI.get("/api/user-businesses/", { params });
      if (res.data && res.data.length > 0) {
        return res.data[0].business_name || "";
      }
      return "";
    } catch (err) {
      // Silently fail - onboarding data may not exist yet
      return "";
    }
  };

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let url = "/api/square/merchants/firestore-customers/";
      if (isAdmin && selectedMerchantId) {
        url = `/api/square/merchants/${encodeURIComponent(selectedMerchantId)}/firestore-customers/`;
      }
      const res = await backendAPI.get(url);
      setPayload(res.data);
    } catch (e) {
      const msg =
        e.response?.data?.error ||
        e.response?.data?.detail ||
        e.message ||
        "Could not load customers.";
      setError(typeof msg === "string" ? msg : "Could not load customers.");
      setPayload(null);
      showToast?.("Could not load customers.", "error");
    } finally {
      setLoading(false);
    }
  }, [isAdmin, selectedMerchantId, showToast]);

  useEffect(() => {
    if (isAdmin && !selectedMerchantId) {
      setLoading(false);
      setPayload(null);
      setError(null);
      return;
    }
    fetchCustomers();
    fetchOnboardingBusinessName().then((name) => setOnboardingName(name));
  }, [fetchCustomers, isAdmin, selectedMerchantId]);

  const rewardCycleVisits =
    payload?.program_reward_visit_size != null
      ? Number(payload.program_reward_visit_size)
      : payload?.program_visit_stamp_size != null
        ? Number(payload.program_visit_stamp_size)
        : null;
  const rewardCyclePoints =
    payload?.program_points_reward_size != null ? Number(payload.program_points_reward_size) : null;
  const businessName =
    (payload?.business_name || onboardingName || "").trim() ||
    (userInfo?.is_admin ? userInfo.username : localStorage.getItem("business_name") || t('customers.defaultStore', 'Store'));

  const sortedCustomers = useMemo(() => {
    const list = [...(payload?.customers || [])];
    if (sortBy === "close_reward") {
      list.sort((a, b) => {
        const ca = closestRewardDistance(a);
        const cb = closestRewardDistance(b);
        if (ca !== cb) return ca - cb;
        return (Number(b.visits_at_merchant) || 0) - (Number(a.visits_at_merchant) || 0);
      });
      return list;
    }
    list.sort((a, b) => {
      const ta = lastVisitIso(a);
      const tb = lastVisitIso(b);
      const da = ta ? new Date(ta).getTime() : 0;
      const db = tb ? new Date(tb).getTime() : 0;
      return db - da;
    });
    return list;
  }, [payload, sortBy]);

  const adminNeedsMerchant = isAdmin && !selectedMerchantId;

  return (
    <div className="klerk-app-container">
      <div className="klerk-sidebar">
        <div className="klerk-logo">Klerk</div>
        <MerchantSidebarMenu dashboardEnd />
      </div>

      <div className="klerk-main-content">
        <div className="klerk-header d-flex justify-content-between align-items-center mb-4">
          <h4 className="page-title m-0">{t('sidebar.customers')}</h4>

          <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
            <div className="user-profile-widget d-flex align-items-center">
            <span className="store-name">{businessName}{" "}</span>
              <span className="status-badge">{t('profile.active')}</span>
              <ProfileDropdown />
            </div>
            <button type="button" className="klerk-btn btn-secondary klerk-header-logout" onClick={logoutHandler}>
              {t('profile.logOut')}
            </button>
          </div>
        </div>

        <p className="klerk-customers-lead">
          {t('customers.lead')}
        </p>

        {isAdmin ? (
          <div className="klerk-card mb-4 m-0">
            <h6 className="card-title-sm">{t('customers.merchantAdmin')}</h6>
            <p className="klerk-customers-lead mb-3" style={{ marginBottom: "0.75rem" }}>
              {t('customers.chooseStore')}
            </p>
            <select
              className="form-select"
              style={{ maxWidth: "420px", borderRadius: "10px", border: "1px solid #e2e8f0" }}
              value={selectedMerchantId}
              onChange={(e) => setSelectedMerchantId(e.target.value)}
              aria-label="Select merchant"
            >
              <option value="">{t('customers.selectMerchant')}</option>
              {merchants.map((m) => (
                <option key={m.id || m.merchant_id} value={m.merchant_id}>
                  {m.business_name || m.merchant_id}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {adminNeedsMerchant ? (
          <div className="klerk-customers-message info mb-0">{t('customers.selectFirst')}</div>
        ) : loading ? (
          <div className="klerk-card m-0 d-flex justify-content-center py-5">
            <Loader />
          </div>
        ) : error ? (
          <div className="klerk-customers-message error mb-0">{error}</div>
        ) : (
          <>
            <div className="klerk-customers-toolbar">
              <div>
                {businessName ? (
                  <h6 className="card-title-sm mb-1" style={{ fontSize: "1.05rem" }}>
                    {businessName}
                    {rewardCycleVisits ? (
                      <span className="text-muted small fw-normal ms-2">
                        · Visit reward: {rewardCycleVisits} visits
                      </span>
                    ) : null}
                    {rewardCyclePoints && Number.isFinite(rewardCyclePoints) ? (
                      <span className="text-muted small fw-normal ms-2">
                        · Points reward: {rewardCyclePoints} pts
                      </span>
                    ) : null}
                  </h6>
                ) : null}
                <p className="program-desc mb-0">
                  {t('customers.count', {
                    count: payload?.count ?? sortedCustomers.length
                  })}
                </p>
              </div>
              <select
                className="form-select form-select-sm"
                style={{ maxWidth: "220px" }}
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                aria-label="Sort customers"
              >
                <option value="recent">{t('customers.sortLastVisit')}</option>
                <option value="close_reward">{t('customers.sortClosestReward')}</option>
              </select>
            </div>

            {sortedCustomers.length === 0 ? (
              <div className="klerk-card banner-card m-0">
                <p className="program-desc mb-0 text-center">
                  {t('customers.noCustomers')}
                </p>
              </div>
            ) : (
              <div className="klerk-card m-0 p-0 overflow-hidden">
                <div className="klerk-customers-table-wrap px-3 py-2">
                  <table className="klerk-customers-table">
                    <thead>
                      <tr>
                        <th>{t('customers.phone', 'Phone')}</th>
                        <th>{t('customers.loyaltyId', 'Loyalty ID')}</th>
                        <th>{t('customers.visitsToNext', 'Visits to next reward')}</th>
                        <th>{t('customers.rewardsRedeemed', 'Rewards redeemed')}</th>
                        <th>{t('customers.lastVisit', 'Last visit')}</th>
                        <th aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {sortedCustomers.map((c, rowIdx) => {
                        const profile = c.firestore_user || {};
                        const untilV = visitsUntilOneToGo(c);
                        const rowKey =
                          profile.id != null
                            ? `p-${profile.id}`
                            : profile.firebase_uid
                              ? `f-${profile.firebase_uid}`
                              : `row-${rowIdx}`;
                        return (
                          <tr key={rowKey}>
                            <td>{formatPhoneDisplay(profile.phone)}</td>
                            <td className="text-break small">{loyaltyIdFromCustomer(c)}</td>
                            <td className="small">
                              {visitsToNextLabel(c)}
                              {untilV === 1 ? (
                                <span className="klerk-badge-warn ms-1">1 to go</span>
                              ) : null}
                            </td>
                            <td>{totalRewardsRedeemed(c)}</td>
                            <td className="text-nowrap small">{formatDateTime(lastVisitIso(c))}</td>
                            <td>
                              <button
                                type="button"
                                className="klerk-customers-detail-btn"
                                onClick={() => setDetailCustomer(c)}
                              >
                                {t('customers.details')}
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <CustomerDetailModal
        show={Boolean(detailCustomer)}
        onHide={() => setDetailCustomer(null)}
        customer={detailCustomer}
        businessName={businessName}
      />
    </div>
  );
}
